// idb-schema.ts (Story 7.3) — the typed MAIN-THREAD entry to Franklin's offline IndexedDB
// database. This is the AC-named file (architecture.md:1329/:1534, F-PWA-IDB at :401).
//
// It owns the npm-Dexie binding and applies the `version().stores()` declarations from the pure
// `idb-schema-def.ts`, so the single `franklin` database is always opened with the one shared
// schema (a drifting/lower version would throw Dexie `VersionError`).
//
// This module is side-effect-light: constructing `db` does NOT force-open IndexedDB — Dexie
// opens lazily on the first operation. So importing it in a jsdom test without `fake-indexeddb`
// won't crash at import time; only a test that actually touches `db` needs the IDB stub.

import Dexie, { type Table, type Transaction } from "dexie";
import {
  DB_NAME,
  SCHEMA_V1,
  SCHEMA_V2,
  SCHEMA_V3,
  type CacheMetaRecord,
  type DraftRecord,
  type MetaEntry,
  type OutboundOp,
} from "./idb-schema-def";

/**
 * Story 19.6 Unit E — the v2 → v3 `.upgrade()` callback. It is a DELIBERATE NO-OP: the upgrade
 * attributes no row, and that is the whole design decision.
 *
 * WHY IT ATTRIBUTES NOTHING (19.6 review). The first cut read `meta.auth_session` /
 * `meta.owner_identity` and stamped every ownerless row with that single device-wide identity. Both
 * keys answer "who last held a session on this DEVICE", never "who wrote this ROW" — so on the
 * shared clinic laptop this unit exists for, one upgrade pass over two clinicians' rows mis-attributes
 * at least one of them. Getting it wrong on `drafts` shows A's PHI to B; getting it wrong on
 * `outbound_queue` REPLAYS A's PHI to the server under B's token, which is the defect Unit E closes,
 * restated by its own migration. A guess is therefore worse than no answer.
 *
 * WHERE ATTRIBUTION HAPPENS INSTEAD — the two moments ownership is actually PROVEN, both of which
 * adopt only an UNATTRIBUTED row and never a foreign one:
 *   1. a 200 from `GET /evaluations/{id}` under this clinician's token, past the endpoint's
 *      `clinician_id` predicate and clinician-only RLS (`useGetEvaluation.writeThroughDraftRow`);
 *   2. this clinician's own save into the row (`draft-producer.upsertDraftData`).
 * Both adopt the evaluation's queued `outbound_queue` ops and its `ledger:*` row alongside the draft,
 * so nothing stays half-attributed and undrainable (`adoptUnattributedEvaluationRows`).
 *
 * Until one of those fires, a pre-v3 row stays UNATTRIBUTED: hidden from the evaluation list, the
 * detail read, the readiness hook and the sync ledger, excluded from the drain, and NEVER DELETED
 * (deleting would destroy un-synchronised clinical work and would empty the `outbound_queue` that
 * gates gh-461's offline restore). The residue — a never-synced draft whose author never returns to
 * this device — is tracked in the deferred-work ledger, not silently dropped.
 *
 * WHY `version(3)` SURVIVES WITHOUT A BACKFILL, and must not be "simplified" back to v2: Dexie
 * throws `VersionError` when a lower version opens a database that already reached a higher one, and
 * devices (team dev browsers, and anything that ran a v3 build) have already migrated to 3. Dropping
 * the version to 2 would hard-fail every one of those opens — the app could not read its own
 * IndexedDB at all. The version boundary is cheap and permanent; the callback on it is empty.
 *
 * Exported, and kept registered rather than deleted, so `idb-migration.test.ts` can drive the REAL
 * callback (a raw `new Dexie` on its own database name cannot reach `FranklinDB`'s internally
 * registered one) and pin that a v2 → v3 open attributes nothing. Re-adding a stamping loop here
 * reds those cases.
 */
export async function upgradeToV3(_tx: Transaction): Promise<void> {
  // Intentionally empty — see the contract above. `_tx` is kept so the signature stays a Dexie
  // upgrade callback and a future *structural* v3 transform has somewhere to go.
}

/**
 * Typed Dexie subclass declaring the four object stores. Every table is keyed by a string
 * (the client-minted UUID for `drafts`/`outbound_queue`; the lookup key for `cache_metadata`/
 * `meta`) — never an auto-increment number, mirroring the proven R6 PoC.
 */
export class FranklinDB extends Dexie {
  drafts!: Table<DraftRecord, string>;
  outbound_queue!: Table<OutboundOp, string>;
  cache_metadata!: Table<CacheMetaRecord, string>;
  meta!: Table<MetaEntry, string>;

  constructor() {
    super(DB_NAME);
    // Each version().stores() is a delta. v1 = drafts + outbound_queue + cache_metadata;
    // v2 ADDS meta (the lossless migration the R6 PoC validated). v3 adds NOTHING — not a store, not
    // an index, and (see `upgradeToV3`) not a data transform either. It exists because devices have
    // already opened this database at 3, and a lower version would throw Dexie `VersionError`. All
    // three come from the shared pure def, the single source of schema truth.
    this.version(1).stores(SCHEMA_V1);
    this.version(2).stores(SCHEMA_V2);
    this.version(3).stores(SCHEMA_V3).upgrade(upgradeToV3);
  }
}

/** The single main-thread handle to the `franklin` database. */
export const db = new FranklinDB();

// --- meta helpers -----------------------------------------------------------
// `meta` is the key/value store for app-level flags that must live in IndexedDB rather than
// localStorage; the force-offline flag that USES it is 7.7 / test tooling. Shapes are kept
// verbatim from the PoC (db.js: `db.meta.put({key,value})` / `row?.value`).

/** Upsert a key/value into `meta`. */
export function setMeta(key: string, value: unknown): Promise<string> {
  return db.meta.put({ key, value });
}

/** Read a `meta` value by key; `undefined` if absent. */
export async function getMeta<T>(key: string): Promise<T | undefined> {
  const row = await db.meta.get(key);
  return row?.value as T | undefined;
}
